import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import {
  Plus, PencilSimple, Trash, Buildings, CopySimple, Eye, ShieldCheck,
  ShieldStar, FilePdf, EnvelopeSimple, Gauge, Sparkle, BellRinging,
} from "@phosphor-icons/react";
import TrustCenterWizardModal from "@/components/TrustCenterWizardModal";
import ClientPushTestModal from "@/components/ClientPushTestModal";

const EMPTY = {
  name: "", contact_email: "", notes: "", industry_hint: "",
  public_status_enabled: false, public_status_password: "",
  sla_target_percent: 99.0,
  monthly_report_enabled: false,
  monthly_report_recipients: "",
};

export default function Clients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [slaMap, setSlaMap] = useState({});      // { client_id -> summary }
  const [busyPdf, setBusyPdf] = useState({});
  const [busyEmail, setBusyEmail] = useState({});
  const [toast, setToast] = useState("");
  const [wizardClient, setWizardClient] = useState(null);
  const [pushTestClient, setPushTestClient] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setItems(data);
      // fetch SLA summaries in parallel (don't block)
      Promise.all(data.map(c =>
        api.get(`/clients/${c.id}/sla`, { params: { days: 30 } })
          .then(r => [c.id, r.data]).catch(() => [c.id, null])
      )).then(rows => {
        const m = {};
        rows.forEach(([id, v]) => { if (v) m[id] = v; });
        setSlaMap(m);
      });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); setError(""); };
  const openEdit = (c) => {
    setForm({
      name: c.name,
      contact_email: c.contact_email || "",
      notes: c.notes || "",
      industry_hint: c.industry_hint || "",
      public_status_enabled: !!c.public_status_enabled,
      public_status_password: "",
      sla_target_percent: c.sla_target_percent ?? 99.0,
      monthly_report_enabled: !!c.monthly_report_enabled,
      monthly_report_recipients: (c.monthly_report_recipients || []).join(", "),
    });
    setEditing(c); setError("");
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = {
        name: form.name,
        contact_email: form.contact_email || null,
        notes: form.notes || null,
        industry_hint: form.industry_hint || null,
        public_status_enabled: !!form.public_status_enabled,
        public_status_password: form.public_status_password || null,
        sla_target_percent: Number(form.sla_target_percent) || 99.0,
        monthly_report_enabled: !!form.monthly_report_enabled,
        monthly_report_recipients: String(form.monthly_report_recipients || "")
          .split(",").map(s => s.trim()).filter(Boolean),
      };
      if (editing === "new") await api.post("/clients", payload);
      else await api.put(`/clients/${editing.id}`, payload);
      setEditing(null); await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete client?")) return;
    await api.delete(`/clients/${id}`); await load();
  };

  const statusUrl = (token) => `${window.location.origin}/status/${token}`;
  const trustUrl = (token) => `${window.location.origin}/trust/${token}`;

  const downloadPdf = async (c) => {
    setBusyPdf({ ...busyPdf, [c.id]: true });
    try {
      const r = await api.get(`/clients/${c.id}/report.pdf`, {
        params: { days: 30 }, responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SentryWatch-${c.name.replace(/\s+/g, "_")}-30d.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000);
      setToast(`Downloaded report for ${c.name}`);
      setTimeout(() => setToast(""), 3000);
    } catch (e) {
      setToast(`PDF failed: ${formatApiError(e.response?.data?.detail) || e.message}`);
      setTimeout(() => setToast(""), 4000);
    } finally {
      setBusyPdf({ ...busyPdf, [c.id]: false });
    }
  };

  const sendNow = async (c) => {
    setBusyEmail({ ...busyEmail, [c.id]: true });
    try {
      const { data } = await api.post(`/clients/${c.id}/send-monthly-report`);
      setToast(data.ok ? `Sent: ${data.message}` : `Send failed: ${data.message}`);
      setTimeout(() => setToast(""), 4000);
    } catch (e) {
      setToast(`Send failed: ${formatApiError(e.response?.data?.detail) || e.message}`);
      setTimeout(() => setToast(""), 4000);
    } finally {
      setBusyEmail({ ...busyEmail, [c.id]: false });
    }
  };

  return (
    <Layout>
      <PageHeader
        title="Clients"
        subtitle="Customers · SLA · public status · monthly reports"
        actions={
          <button onClick={openNew} data-testid="clients-new-btn"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold transition-colors">
            <Plus size={14} weight="bold" /> New client
          </button>
        }
      />
      <div className="p-6">
        {toast && (
          <div className="mb-4 text-xs px-3 py-2 border border-brand-cyan/40 bg-brand-cyan/10 text-brand-cyan rounded"
            data-testid="clients-toast">{toast}</div>
        )}
        {loading ? <div className="text-sm text-slate-500">loading…</div>
         : items.length === 0 ? (
          <div className="sw-card border-dashed p-16 text-center">
            <Buildings size={36} weight="duotone" className="text-brand-cyan mx-auto mb-3" />
            <h3 className="font-display text-lg font-bold tracking-tight mb-1">No clients yet</h3>
            <p className="text-sm text-slate-400 mb-4">Add your first client to start grouping jobs and monitors.</p>
            <button onClick={openNew} data-testid="clients-empty-cta"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">
              <Plus size={14} weight="bold" /> Add client
            </button>
          </div>
        ) : (
          <div className="sw-card overflow-hidden">
            <table className="w-full text-sm" data-testid="clients-table">
              <thead className="bg-background-deep/60">
                <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
                  <th className="text-left py-3 px-4">Name</th>
                  <th className="text-left py-3 px-4">SLA (30d)</th>
                  <th className="text-left py-3 px-4">Public status</th>
                  <th className="text-left py-3 px-4">Monthly report</th>
                  <th className="text-right py-3 px-4 w-80">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => {
                  const sla = slaMap[c.id]?.summary;
                  return (
                    <tr key={c.id} className="border-t border-border hover:bg-white/5">
                      <td className="py-3 px-4">
                        <div className="text-white" data-testid={`client-name-${c.id}`}>{c.name}</div>
                        <div className="text-[10px] text-slate-500 truncate max-w-[260px]">
                          {c.contact_email || "no contact email"}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <SlaBadge sla={sla} target={c.sla_target_percent} clientId={c.id} />
                      </td>
                      <td className="py-3 px-4">
                        {c.public_status_enabled && c.public_status_token ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-400">LIVE</span>
                              <a href={statusUrl(c.public_status_token)} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-brand-cyan hover:text-white truncate max-w-[200px] inline-flex items-center gap-1"
                                data-testid={`client-status-link-${c.id}`}>
                                <Eye size={12} weight="bold" /> /status/{c.public_status_token.slice(0, 10)}…
                              </a>
                              <button onClick={() => { navigator.clipboard.writeText(statusUrl(c.public_status_token)); setToast("Status URL copied"); setTimeout(() => setToast(""), 1500); }}
                                className="text-xs text-slate-400 hover:text-brand-cyan" title="Copy live status URL"
                                data-testid={`client-copy-${c.id}`}>
                                <CopySimple size={12} weight="bold" />
                              </button>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-cyan">TRUST</span>
                              <a href={trustUrl(c.public_status_token)} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-brand-cyan hover:text-white truncate max-w-[200px] inline-flex items-center gap-1"
                                data-testid={`client-trust-link-${c.id}`}>
                                <ShieldStar size={12} weight="bold" /> /trust/{c.public_status_token.slice(0, 10)}…
                              </a>
                              <button onClick={() => { navigator.clipboard.writeText(trustUrl(c.public_status_token)); setToast("Trust URL copied"); setTimeout(() => setToast(""), 1500); }}
                                className="text-xs text-slate-400 hover:text-brand-cyan" title="Copy 90-day trust report URL"
                                data-testid={`client-trust-copy-${c.id}`}>
                                <CopySimple size={12} weight="bold" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-500">disabled</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {c.monthly_report_enabled ? (
                          <div>
                            <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-400">ON</span>
                            <div className="text-[10px] text-slate-500 truncate max-w-[200px]">
                              {(c.monthly_report_recipients || []).join(", ") || "no recipients"}
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-500">off</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <button onClick={() => setWizardClient(c)}
                          data-testid={`client-wizard-${c.id}`}
                          title="AI-generated 90-day Trust Center report"
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] border border-brand-cyan/40 bg-brand-cyan/5 text-brand-cyan hover:bg-brand-cyan/15 rounded mr-1 transition-colors">
                          <Sparkle size={12} weight="bold" /> Wizard
                        </button>
                        <button onClick={() => setPushTestClient(c)}
                          data-testid={`client-push-test-${c.id}`}
                          title="Send a test push notification to this client's on-call rotation"
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] border border-border hover:border-brand-cyan/40 hover:bg-brand-cyan/10 rounded mr-1 transition-colors text-slate-200">
                          <BellRinging size={12} weight="bold" /> Ping
                        </button>
                        <button onClick={() => downloadPdf(c)} disabled={busyPdf[c.id]}
                          data-testid={`client-pdf-${c.id}`}
                          title="Download SLA / MTTR PDF (30 days)"
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] border border-border hover:border-brand-cyan/40 hover:bg-brand-cyan/10 rounded mr-1 transition-colors text-slate-200">
                          <FilePdf size={12} weight="bold" /> {busyPdf[c.id] ? "…" : "PDF"}
                        </button>
                        <button onClick={() => sendNow(c)} disabled={busyEmail[c.id]}
                          data-testid={`client-email-${c.id}`}
                          title="Email monthly report now"
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] border border-border hover:border-brand-cyan/40 hover:bg-brand-cyan/10 rounded mr-1 transition-colors text-slate-200">
                          <EnvelopeSimple size={12} weight="bold" /> {busyEmail[c.id] ? "…" : "Email"}
                        </button>
                        <button onClick={() => openEdit(c)} data-testid={`client-edit-${c.id}`}
                          className="p-1.5 text-slate-300 hover:text-white"><PencilSimple size={14} /></button>
                        <button onClick={() => remove(c.id)} data-testid={`client-delete-${c.id}`}
                          className="p-1.5 text-slate-300 hover:text-red-400 ml-1"><Trash size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-30 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="sw-card w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="client-form">
            <h3 className="font-display text-lg font-bold tracking-tight">{editing === "new" ? "New client" : "Edit client"}</h3>
            <FieldC label="Name" required>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" />
            </FieldC>
            <FieldC label="Contact email">
              <input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="client-contact-input" />
            </FieldC>
            <FieldC label="Notes">
              <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="client-notes-input" />
            </FieldC>
            <FieldC label="Industry hint (informs AI narrative + cover art)">
              <input value={form.industry_hint}
                placeholder="healthcare, legal, manufacturing, saas…"
                onChange={(e) => setForm({ ...form, industry_hint: e.target.value })}
                data-testid="client-industry-input" />
            </FieldC>

            <div className="pt-3 border-t border-border space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-brand-cyan">
                <Gauge size={12} weight="duotone" /> SLA TARGET
              </div>
              <FieldC label="Target uptime % (used for badge & PDF)">
                <input type="number" step="0.01" min="0" max="100"
                  value={form.sla_target_percent}
                  onChange={(e) => setForm({ ...form, sla_target_percent: e.target.value })}
                  data-testid="client-sla-target-input" />
              </FieldC>
            </div>

            <div className="pt-3 border-t border-border space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-brand-cyan">
                <EnvelopeSimple size={12} weight="duotone" /> MONTHLY PDF REPORT
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input type="checkbox" checked={form.monthly_report_enabled}
                  onChange={(e) => setForm({ ...form, monthly_report_enabled: e.target.checked })}
                  data-testid="client-monthly-enabled-checkbox" />
                Email PDF on the 1st of each month
              </label>
              {form.monthly_report_enabled && (
                <FieldC label="Recipients (comma-sep emails)">
                  <input value={form.monthly_report_recipients}
                    placeholder="ops@msp.com, customer@acme.com"
                    onChange={(e) => setForm({ ...form, monthly_report_recipients: e.target.value })}
                    data-testid="client-monthly-recipients-input" />
                </FieldC>
              )}
            </div>

            <div className="pt-3 border-t border-border space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-brand-cyan">
                <ShieldCheck size={12} weight="duotone" /> PUBLIC STATUS PAGE
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-200">
                <input type="checkbox" checked={form.public_status_enabled}
                  onChange={(e) => setForm({ ...form, public_status_enabled: e.target.checked })}
                  data-testid="client-public-enabled-checkbox" />
                Enable public status page
              </label>
              {form.public_status_enabled && (
                <>
                  <FieldC label={`Password${editing !== "new" ? " (leave blank to keep current)" : ""}`}>
                    <input type="password" minLength={editing === "new" ? 0 : 0}
                      value={form.public_status_password}
                      placeholder={editing !== "new" ? "leave blank to keep" : "(optional — leave blank for open page)"}
                      onChange={(e) => setForm({ ...form, public_status_password: e.target.value })}
                      data-testid="client-public-password-input" />
                  </FieldC>
                  {editing !== "new" && editing.public_status_token && (
                    <div className="text-[11px] font-mono text-slate-400 break-all bg-background-deep border border-border rounded p-2">
                      URL: <span className="text-brand-cyan">{window.location.origin}/status/{editing.public_status_token}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {error && <div className="text-xs text-red-400">{error}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditing(null)} data-testid="client-cancel-btn"
                className="px-3 py-1.5 text-sm border border-border hover:bg-white/5 rounded-md">Cancel</button>
              <button type="submit" data-testid="client-save-btn"
                className="px-3 py-1.5 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">Save</button>
            </div>
          </form>
        </div>
      )}
      {wizardClient && (
        <TrustCenterWizardModal
          client={wizardClient}
          onClose={() => setWizardClient(null)}
          onSent={() => { setWizardClient(null); load(); setToast("Trust Report sent"); setTimeout(() => setToast(""), 2500); }}
        />
      )}
      {pushTestClient && (
        <ClientPushTestModal
          client={pushTestClient}
          onClose={() => setPushTestClient(null)}
        />
      )}
    </Layout>
  );
}

function SlaBadge({ sla, target, clientId }) {
  if (!sla) return <span className="text-xs text-slate-500">—</span>;
  const pct = sla.overall_uptime_pct;
  if (pct == null) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] border rounded bg-slate-500/15 text-slate-300 border-slate-500/40"
        data-testid={`sla-badge-${clientId}`}>
        no data
      </span>
    );
  }
  const meets = pct >= (target ?? 99);
  const tone = meets
    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
    : "bg-red-500/15 text-red-300 border-red-500/40";
  const mttrLabel = sla.mttr_seconds != null
    ? (sla.mttr_seconds < 60 ? `${sla.mttr_seconds}s`
      : sla.mttr_seconds < 3600 ? `${Math.round(sla.mttr_seconds / 60)}m`
      : `${(sla.mttr_seconds / 3600).toFixed(1)}h`)
    : "—";
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] border rounded ${tone}`}
        data-testid={`sla-badge-${clientId}`}>
        <ShieldStar size={10} weight="bold" /> {pct.toFixed(2)}% / target {Number(target ?? 99).toFixed(1)}%
      </span>
      <span className="text-[9px] text-slate-500">
        MTTR {mttrLabel} · {sla.incidents_closed ?? 0} closed · {sla.incidents_open ?? 0} open
      </span>
    </div>
  );
}

function FieldC({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {React.cloneElement(children, {
        className: "w-full bg-background-deep border border-border rounded px-3 py-2 text-sm focus:outline-none focus:border-brand-cyan transition-colors",
      })}
    </div>
  );
}
